import { NavLink, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { useAuth, hasPermission } from '@/contexts/AuthContext'
import {
  LayoutDashboard,
  Users,
  Coins,
  Settings,
  UserPlus,
  FileText,
  X,
  Bot,
  Rocket,
  TrendingUp,
  Vault,
  Activity,
  Gauge,
} from 'lucide-react'

type NavItem = {
  name: string
  href: string
  icon: typeof LayoutDashboard
  minRole?: 'admin' | 'super_admin'
}

// Viewers see only items without a minRole: Dashboard, Coins, Settings,
// Infrastructure, Global Metrics. URL path for Coins stays /tokens even
// though the UI label says "Coins". Viewer access to /infrastructure and
// /global-metrics also requires VIEWER_ALLOWED_PATHS in DashboardLayout.tsx.
const navigation: NavItem[] = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Global Metrics', href: '/global-metrics', icon: Gauge },
  { name: 'Infrastructure', href: '/infrastructure', icon: Activity },
  { name: 'Users', href: '/users', icon: Users, minRole: 'admin' },
  { name: 'Coins', href: '/tokens', icon: Coins },
  { name: 'Invites', href: '/invites', icon: UserPlus, minRole: 'admin' },
  { name: 'Audit Logs', href: '/audit', icon: FileText, minRole: 'admin' },
  { name: 'Seedbot', href: '/seedbot', icon: Bot, minRole: 'admin' },
  { name: 'pump.fun', href: '/pumpfun', icon: Rocket, minRole: 'admin' },
  { name: 'Positions', href: '/positions', icon: TrendingUp, minRole: 'admin' },
  { name: 'Treasury', href: '/treasury', icon: Vault, minRole: 'admin' },
  { name: 'Settings', href: '/settings', icon: Settings },
]

interface SidebarProps {
  isOpen?: boolean
  onClose?: () => void
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const location = useLocation()
  const { adminUser } = useAuth()

  const visibleNavigation = navigation.filter(
    (item) => !item.minRole || hasPermission(adminUser?.role, item.minRole)
  )

  const handleNavClick = () => {
    if (onClose) {
      onClose()
    }
  }

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 bg-card border-r border-border transform transition-transform duration-300 ease-in-out',
          'lg:translate-x-0',
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
      >
        <div className="flex h-16 items-center justify-between px-6 border-b border-border">
          <div className="flex items-center gap-2">
            <img src="/dextoro-logo.png" alt="dextoro" className="hidden dark:block h-7" />
            <img src="/dextoro-logo-black.png" alt="dextoro" className="block dark:hidden h-7" />
            {adminUser?.role !== 'viewer' && (
              <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                Admin
              </span>
            )}
          </div>
          {/* Mobile close button */}
          <button
            onClick={onClose}
            className="lg:hidden p-2 rounded-md hover:bg-muted"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {visibleNavigation.map((item) => {
            const isActive = location.pathname === item.href
            return (
              <NavLink
                key={item.name}
                to={item.href}
                onClick={handleNavClick}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors min-h-[44px]',
                  isActive
                    ? 'bg-brand-fg/10 text-brand-fg'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                )}
              >
                <item.icon className="h-5 w-5" />
                {item.name}
              </NavLink>
            )
          })}
        </nav>

        <div className="p-4 border-t border-border">
          <div className="px-3 py-2 rounded-lg bg-muted/50">
            <p className="text-xs text-muted-foreground">Environment</p>
            <p className="text-sm font-medium text-brand-fg">Production</p>
          </div>
        </div>
      </aside>
    </>
  )
}
